import moment from 'moment';
import { List, Map, OrderedMap } from 'immutable';

import { YesNoUnknown } from '../common/Models';

import genEnum from '../services/genEnum';
import genModel from '../services/genModel';
import { formatContractId } from '../services/sharedUtils';

export const InvoiceLineType = genEnum(
  'invoiceitem',
  'subscription'
);

export const InvoiceLineMetadata = genModel({
  type: { type: 'string', default: '' }
});

export class InvoicePeriod extends genModel({
  start: { type: 'moment', default: moment.utc() },
  end: { type: 'moment', default: moment.utc() }
}) {}

export class InvoiceLine extends genModel({
  amount: { type: 'number', default: 0 },
  currency: { type: 'string', default: 'EUR' },
  description: { type: 'string', default: '' },
  id: { type: 'string', default: 'PLACEHOLDER_ID' },
  metadata: { type: 'model', model: InvoiceLineMetadata, default: InvoiceLineMetadata.empty },
  period: { type: 'model', model: InvoicePeriod, default: InvoicePeriod.empty },
  type: { type: 'enum', enum: InvoiceLineType, default: InvoiceLineType.invoiceitem }
}) {}

export const InvoiceStatus = genEnum(
  'draft',
  'open',
  'paid',
  'uncollectible',
  'void'
);

export class Invoice extends genModel({
  amount_due: { type: 'number', default: 0 },
  amount_paid: { type: 'number', default: 0 },
  amount_remaining: { type: 'number', default: 0 },
  attempt_count: { type: 'number', default: 0 },
  attempted: { type: 'bool', default: false },
  auto_advance: { type: 'bool', default: false },
  currency: { type: 'string', default: 'EUR' },
  created: { type: 'moment', default: moment.utc() },
  description: { type: 'string', default: '' },
  due_date: { type: 'moment', default: null, optional: true },
  forgiven: { type: 'bool', default: false },
  id: { type: 'string', default: 'PLACEHOLDER_ID' },
  lines: { type: 'List<model>', model: InvoiceLine, default: List() },
  next_payment_attempt: { type: 'moment', default: null, optional: true },
  number: { type: 'string', default: 'PLACEHOLDER' },
  paid: { type: 'bool', default: false },
  // NOTE: period_start and period_end may point to the same day. Use period() getter instead.
  period_end: { type: 'moment', default: moment.utc() },
  period_start: { type: 'moment', default: moment.utc() },
  requires_action: { type: 'bool', default: false },
  status: { type: 'enum', enum: InvoiceStatus, default: InvoiceStatus.draft },
  subtotal: { type: 'number', default: 0 },
  total: { type: 'number', default: 0 }
}) {
  static fromJSON(json) {
    return new Invoice(Invoice.parseJSON({
      ...json,
      description: json.description ?? ''
    }));
  }

  get period() {
    // Get period from the subscription invoice item.
    const subscriptionItem = this.lines.find(it => it.type === InvoiceLineType.subscription);
    if (subscriptionItem) {
      return subscriptionItem.period;
    }
    // Fallback to invoice period properties.
    // They can point to the same day in some invoices, but it's better than nothing.
    return new InvoicePeriod({
      start: this.period_start,
      end: this.period_end
    });
  }
}

export const DepositStatus = genEnum(
  'ACTIVE',
  'CANCELED',
  'DISTRAINT_REGISTER_CHECK',
  'PENDING',
  'ACTIVATION_INCOMPLETE',
  'ACTIVATION_INCOMPLETE_CAN_NOT_RETRY',
  'ACTIVATION_REQUIRES_ACTION',
  'RECURRING_FEE_CANNOT_BE_CHARGED',
  'RECURRING_FEES_REQUIRE_ACTION'
);

export const DepositCancellationStatus = genEnum(
  'ADMIN',
  'ADMIN_DISTRAINT_REGISTER_NOT_CLEAN',
  'FAILED_PAYMENT',
  'NOT_CANCELLED',
  'TENANT'
);

export const DepositBillingInterval = genEnum(
  'YEARLY',
  'MONTHLY'
);

export const DepositLevel = genEnum(
  'ONE_MONTH',
  'TWO_MONTHS',
  'THREE_MONTHS'
);

export function levelToMonths(level) {
  switch (level) {
    case DepositLevel.ONE_MONTH: return 1;
    case DepositLevel.TWO_MONTHS: return 2;
    case DepositLevel.THREE_MONTHS: return 3;
    default: return 0;
  }
}

export class Deposit extends genModel({
  deposit_id: { type: 'string', default: 'PLACEHOLDER_ID' }, // uuid
  contract_id: { type: 'string', default: '' },
  created: { type: 'moment', default: moment.utc() },

  status: {
    type: 'enum',
    enum: DepositStatus,
    default: DepositStatus.PENDING
  },
  cancellation_status: {
    type: 'enum',
    enum: DepositCancellationStatus,
    default: DepositCancellationStatus.NOT_CANCELLED
  },
  canceled: { type: 'moment', default: moment.utc(), optional: true },

  user_id: { type: 'string', default: 'PLACEHOLDER_ID' },
  tenant_name: { type: 'string', default: '' },
  tenant_email: { type: 'string', default: '' },
  tenant_phone_number: { type: 'string', default: '' },

  landlord_id: { type: 'string', default: null, optional: true },
  landlord_name: { type: 'string', default: '' },
  landlord_email: { type: 'string', default: '' },
  landlord_phone_number: { type: 'string', default: '' },

  property_id: { type: 'string', default: null, optional: true },
  property_address: { type: 'string', default: '' },
  property_rent: { type: 'number', default: 0 },
  property_rent_currency: { type: 'string', default: 'EUR' },
  lease_start_time: { type: 'moment', default: moment.utc() },

  invoices: { type: 'OrderedMap<model,string>', model: Invoice, default: OrderedMap() },
  setup_fee: { type: 'number', default: 0 },
  recurring_fee: { type: 'number', default: 0 },
  currency: { type: 'string', default: 'EUR' },
  billing_interval: {
    type: 'enum',
    enum: DepositBillingInterval,
    default: DepositBillingInterval.MONTHLY
  },
  level: {
    type: 'enum',
    enum: DepositLevel,
    default: DepositLevel.ONE_MONTH
  },

  landlord_accepts: {
    type: 'enum',
    enum: YesNoUnknown,
    default: YesNoUnknown.UNKNOWN
  },
  landlord_answered: { type: 'moment', default: moment.utc() },

  setup_fee_to_be_paid: { type: 'moment', default: moment.utc() },
  setup_fee_paid: { type: 'moment', default: null, optional: true }
}) {
  static fromJSON(json) {
    return new Deposit(Deposit.parseJSON({
      ...json,
      invoices: (json.invoices ?? [])
        .map(it => [it.id, it])
    }));
  }

  get formatted_contract_id() { // eslint-disable-line camelcase
    return formatContractId(this.contract_id);
  }
}

export class DepositDraft extends genModel({
  level: {
    type: 'enum',
    enum: DepositLevel,
    default: DepositLevel.TWO_MONTHS
  },
  billing_interval: {
    type: 'enum',
    enum: DepositBillingInterval,
    default: DepositBillingInterval.MONTHLY
  },
  landlord_name: { default: '', type: 'string' },
  landlord_email: { default: '', type: 'string' },
  landlord_phone_number: { default: '', type: 'string' },
  tenant_name: { default: '', type: 'string' },
  tenant_email: { default: '', type: 'string' },
  tenant_phone_number: { default: '', type: 'string' },
  property_id: { default: '', type: 'string' },
  property_address: { default: '', type: 'string' },
  property_rent: { default: 0, type: 'number' },
  property_rent_currency: { default: 'EUR', type: 'string' },
  // Not truly optional, but we need to allow null value for the DatePicker.
  lease_start_time: { default: null, type: 'moment', optional: true }
}) {}

export class DepositLevelItemData extends genModel({
  months: { type: 'number', default: 0 },
  monthly_fee: { type: 'number', default: 0 },
  yearly_fee: { type: 'number', default: 0 },
  level: {
    type: 'enum',
    enum: DepositLevel,
    default: DepositLevel.TWO_MONTHS
  }
}) {}

export class DepositPricing extends genModel({
  currency: { default: 'EUR', type: 'string' },
  setup_fee: { type: 'number', default: 0 },
  recurring_fees: {
    type: 'Map<Map<number,string>,string>',
    default: Map({
      [DepositBillingInterval.MONTHLY]: Map({
        [DepositLevel.ONE_MONTH]: 0,
        [DepositLevel.TWO_MONTHS]: 0,
        [DepositLevel.THREE_MONTHS]: 0
      }),
      [DepositBillingInterval.YEARLY]: Map({
        [DepositLevel.ONE_MONTH]: 0,
        [DepositLevel.TWO_MONTHS]: 0,
        [DepositLevel.THREE_MONTHS]: 0
      })
    })
  },
  level_items: { type: 'List<model>', model: DepositLevelItemData, default: List() }
}) {
  static fromJSON(json) {
    // eslint-disable-next-line camelcase
    const level_items = DepositLevel.values.toList()
      .map((level, index) => {
        const monthlyFee = json.recurring_fees?.[DepositBillingInterval.MONTHLY][level];
        const yearlyFee = json.recurring_fees?.[DepositBillingInterval.YEARLY][level];
        const months = index + 1;
        return {
          months,
          monthly_fee: monthlyFee,
          yearly_fee: yearlyFee,
          level
        };
      });

    return new DepositPricing(DepositPricing.parseJSON({ ...json, level_items }));
  }
}
